<script>
import useSharedData from "@/use/SharedData.js";
import useTools from "@/use/Tools.js";
import useSharedMethod from "@/use/SharedMethod.js";
import {computed, ref, watch} from "vue";
import {useSecurityStore} from "@/stores/Security.js";
import axios from "axios";
import {useSagePaySlipStore} from "@/stores/Integration/Sage/SagePaySlip";

export default {
    props: {
        vacation: Boolean,
        intranet: {
            type: Boolean,
            default: false
        },
    },
    components: {},
    setup(props) {
        const {loading, total, filter, itemsPerPage} = useSharedData();
        const sagePaySlipStore = useSagePaySlipStore();
        const securityStore = useSecurityStore();
        const {$i18n, $router} = useTools();
        const {formatedDate, transformFilterValues} = useSharedMethod();
        
        const rootPath = computed(() => props.intranet ? 'intranet_sage_pay_slips' : 'sage_pay_slips');
        
        const headers = ref([
            {
                text: $i18n.t("common.code"),
                align: "start",
                sortable: true,
                value: "workerCode",
                width: "0%",
            },
            {
                text: $i18n.t("module.worker"),
                align: "start",
                sortable: true,
                value: "workerLastName",
                width: "0%",
            },
            {
                text: $i18n.t("module.occupation"),
                align: "start",
                sortable: true,
                value: "workerOccupationName",
                width: "0%",
            },
            {
                text: $i18n.t("module.organizational_chart"),
                align: "start",
                sortable: true,
                value: "workerOrganizationalChartName",
                width: "0%",
            },
            {
                text: $i18n.t("common.enter_date"),
                align: "start",
                sortable: true,
                value: "workerRealEnterDate",
                width: "0%",
            },
            {
                text: $i18n.t("common.month"),
                align: "start",
                sortable: true,
                value: "yearMonth",
                width: "0%",
            },
            {
                text: $i18n.t("common.lastModified"),
                align: "start",
                sortable: true,
                value: "lastModified",
                width: "0%",
            },
            {
                text: $i18n.t("common.label"),
                align: "start",
                sortable: true,
                value: "name",
            },
        ]);
        
        const searchParams = ref({
            sortBy: "lastModified",
            descending: true,
            page: 1,
            itemsPerPage: itemsPerPage
        });
        
        function handleSearch(params) {
            loading.value = true;
            sagePaySlipStore.setSagePaySlips([]);
            total.value = 0;
            
            filter.value.myData = props.intranet;
            
            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;
            
            axios.get('/sage/paySlips', {params: transformFilterValues(filter.value)}).then((response) => {
                sagePaySlipStore.setSagePaySlips(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function findAll() {
            handleSearch(searchParams.value)
        }
        
        watch(() => [filter.value], () => {
            searchParams.page = 1;
            findAll();
        }, {deep: true, immediate: true});
        
        watch(searchParams, (newParams) => {
            handleSearch(newParams);
        }, {deep: true});
        
        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                filter.value[key] = null;
            });
        }
        
        function view(item) {
            $router.push({name: `${rootPath.value}.view`, params: {id: item.id}});
        }
        
        
        return {
            rootPath,
            securityStore,
            sagePaySlipStore,
            loading,
            total,
            filter,
            headers,
            searchParams,
            clearFilter,
            findAll,
            view,
            formatedDate,
        }
    },
};
</script>

<template>
    <div>
        <w-app
            :title="$t('integration.sage.pay_slip')"
            :total="total"
            :show-filter="!intranet"
            :filter="filter"
            @clearFilter="clearFilter"
        >
            
            <template v-slot:filter>
            </template>
            
            <template v-slot:leftToolbar>
            
            </template>
            
            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-import
                    :show="securityStore.authorities['SAVE_PAY_SLIP']"
                    @press="$router.push({name: `${rootPath}.upload`})"
                />
            </template>
            
            <template v-slot:content>
                <w-server-data-table
                    v-model="searchParams"
                    :headers="headers"
                    :items="sagePaySlipStore.sagePaySlips"
                    :loading="loading"
                    :total="total"
                    show-validation
                    :root-path="rootPath"
                >
                    <template v-slot:[`item.workerCode`]="{ item }">
                        <w-chip
                            :text="item.workerCode"
                            :icon="item.workerHead ? 'mdi-account-supervisor-circle' : null"
                            icon-color="teal"
                        />
                    </template>
                    <template v-slot:[`item.workerLastName`]="{ item }">
                        <w-inline-text bold :text="item.workerName"/>
                    </template>
                    <template v-slot:[`item.workerOccupationName`]="{ item }">
                        <w-inline-text :text="item.workerOccupationName"/>
                    </template>
                    <template v-slot:[`item.workerOrganizationalChartName`]="{ item }">
                        <w-inline-text :text="item.workerOrganizationalChartName"/>
                    </template>
                    <template v-slot:[`item.workerRealEnterDate`]="{ item }">
                        <w-inline-text :text="formatedDate(item.workerRealEnterDate)"/>
                    </template>
                    <template v-slot:[`item.yearMonth`]="{ item }">
                        <w-chip
                            color="teal"
                            :text="item.yearMonth"
                        />
                    </template>
                    <template v-slot:[`item.lastModified`]="{ item }">
                        <w-inline-text :text="formatedDate(item.lastModified)"/>
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                        <w-inline-text
                            clickable
                            icon="mdi-file-pdf-box"
                            icon-color="red"
                            :text="item.name"
                            @press="view(item)"
                        />
                    </template>
                </w-server-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>